import type { PropsWithChildren, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@flywire/headlessui-tailwindcss';
import type {
  CardElementEvent,
  CardElementRef,
  CardNewMandateError,
  CardNewMandateEvent,
  CardNewMandateRef,
  OnBinLookupResult,
  OnSuccessPayload,
} from '@flywire/internal-react-elements';
import { CardNewMandate } from '@flywire/internal-react-elements';
import classNames from 'classnames';

import { useDispatch } from 'store/configureStore';

import { Spinner } from 'components/Spinner/Spinner';
import { useCreateCheckoutSessions } from 'hooks/useCheckoutSessions/useCreateCheckoutSession';
import type { CardPaymentMethod, CardSettings, CheckoutSession } from 'models';
import { getAccessToken } from 'selectors/entities/authentication/authentication';
import { useTranslations } from 'utils';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

import { logger } from 'services/logger/logger';
import type {
  CardInstructionsAttributes,
  CardTokenizationEvent,
} from 'services/tokenization/models/models';

import { newNotification } from '../../actions';
import { CreditCardListItem } from './components/CreditCardListItem/CreditCardListItem';
import { CreditCardsProvider, useCreditCards } from './hooks/useCreditCards';

import { CardForm } from 'components/CardTokenization/CardForm/CardForm';
import { FLYWIRE_JS_ENVIRONMENT } from 'constants/config';
import type { SenderFields } from 'domain/recurringReceivables/repository/types';
import { ExclamationCircle } from 'icons/ExclamationCircle';
import type { CheckoutSessionType } from 'services/sessions/sessions';
import { isCardValid } from 'utils/cardSettings/cardSettings';
import './CreditCards.scss';

type AddCardElementProps = {
  buttonLabel: string;
  cardSettings?: CardSettings;
  currency: string;
  onSuccess: (
    data: {
      confirm: {
        url: string;
        method: string;
      };
    },
    sessionId: string,
  ) => void;
  onBinLookup?: (bin: OnBinLookupResult) => void;
  onError?: () => void;
  onEvent?: (event: CardElementEvent) => void;
  recipientId: string;
  sessionType: CheckoutSessionType;
  senderFields?: SenderFields;
};

const AddCardElement = ({
  buttonLabel,
  cardSettings,
  currency,
  onError,
  onEvent,
  onSuccess,
  recipientId,
  sessionType,
  senderFields,
}: AddCardElementProps) => {
  const accessToken = useSelector(getAccessToken);
  const i18n = useTranslations();
  const { showTokenizationForm } = useCreditCards();
  const [sessionId, setSessionId] = useState('');
  const [showInvalidCardError, setShowInvalidCardError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [onBinLookupResult, setOnBinLookupResult] = useState<
    'processing' | 'error' | 'success'
  >('success');
  const ref = useRef<CardElementRef>(null);

  const { createCheckoutSession } = useCreateCheckoutSessions({
    onSuccess: (session: CheckoutSession) => {
      setSessionId(session.id);
    },
    onError: () => {
      onError?.();
    },
  });

  const handleSucces = (payload: OnSuccessPayload) => {
    onSuccess(payload, sessionId);
  };

  const createNewSession = () => {
    setIsLoading(true);
    createCheckoutSession({
      accessToken,
      locale: i18n.currentLocale,
      recipientId,
      sessionType,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (showTokenizationForm) {
      createNewSession();
    } else {
      setSessionId('');
    }
  }, [showTokenizationForm]);

  const onSubmit = () => {
    ref.current?.submit();
  };

  const handleBinLookup = (result: OnBinLookupResult) => {
    setOnBinLookupResult('processing');
    if (result.status === 'success' && cardSettings) {
      if (
        !isCardValid(
          result.result.cardScheme,
          result.result.cardClassification,
          cardSettings,
        )
      ) {
        setShowInvalidCardError(true);
        setOnBinLookupResult('error');
        return;
      }
    }
    setOnBinLookupResult('success');
    setShowInvalidCardError(false);
  };

  return showTokenizationForm ? (
    <CardForm
      cardFormRef={ref}
      onEndLoading={() => setIsLoading(false)}
      onStartLoading={() => setIsLoading(true)}
      onSuccess={handleSucces}
      sessionId={sessionId}
      currency={currency}
      onBinLookup={handleBinLookup}
      onError={onError}
      onEvent={onEvent}
      senderFields={senderFields}
    >
      {isLoading ? (
        <Spinner className="h-40" />
      ) : (
        <>
          <CardForm.Form
            buttonLabel={buttonLabel}
            buttonDisabled={
              onBinLookupResult !== 'success' || showInvalidCardError
            }
            isLoading={onBinLookupResult === 'processing'}
            onSubmit={onSubmit}
          />
          {showInvalidCardError && (
            <div
              className="w-full bg-[#FBEAE5] mt-4"
              data-testid="invalidCardMessage"
            >
              <div className="flex items-center gap-x-2 p-3">
                <ExclamationCircle
                  aria-hidden={true}
                  className="text-red-600"
                />
                <div className="text-sm">
                  {i18n.t('cardForm.error.invalidCard')}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </CardForm>
  ) : null;
};

type CreditCardListProps = {
  currency?: string;
  recipientId: string;
  selectedCard?: CardPaymentMethod;
  onEventCVV?: (event: CardNewMandateEvent) => void;
  onErrorCVV?: (error?: CardNewMandateError) => void;
  onSelectedCardChange?: (card?: CardPaymentMethod) => void;
  onSuccessCVV: (
    data: { confirm: { url: string; method: string } },
    sessionId: string,
  ) => void;
};

const CreditCardList = ({
  currency,
  recipientId,
  selectedCard,
  onEventCVV,
  onErrorCVV,
  onSelectedCardChange,
  onSuccessCVV,
}: CreditCardListProps) => {
  const {
    cards,
    cvvRef,
    hasAddForm,
    isCVVLoading,
    isSubmitting,
    showTokenizationForm,
    setCvvEvent,
    setIsCVVLoading,
    setIsSubmitting,
    setShowTokenizationForm,
  } = useCreditCards();

  const dispatch = useDispatch();
  const i18n = useTranslations();
  const accessToken = useSelector(getAccessToken);
  const [sessionId, setSessionId] = useState('');
  const [selectedCreditCard, setSelectedCreditCard] = useState<
    CardPaymentMethod | undefined
  >(selectedCard || cards[0]);
  const onSuccessCreateSession = (session: CheckoutSession) => {
    setSessionId(session.id);
  };

  const errorNotification = () => {
    dispatch(
      newNotification({
        message: 'notifications.error_card_selection_validation',
        type: 'error',
      }),
    );
  };

  const { createCheckoutSession } = useCreateCheckoutSessions({
    onSuccess: onSuccessCreateSession,
    onError: () => {
      onErrorCVV?.();
    },
  });

  const createNewSession = (paymentMethodToken: string) => {
    setIsCVVLoading(true);
    createCheckoutSession({
      accessToken,
      locale: i18n.currentLocale,
      paymentMethodToken,
      recipientId,
      sessionType: 'new_mandate',
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (selectedCreditCard) {
      createNewSession(selectedCreditCard.token);
    }
  }, []);

  if (!cards.length) {
    return null;
  }

  const onSuccessCVVValidation = (data: {
    confirm: { url: string; method: string };
  }) => {
    setIsSubmitting(false);
    onSuccessCVV(data, sessionId);
  };

  const onErrorCVVValidation = async (error: CardNewMandateError) => {
    setIsSubmitting(false);
    if (selectedCreditCard) {
      if (error.type === 'internal_error') {
        errorNotifier.notify(error);
      }
    }
    onErrorCVV?.(error);
    error.type !== 'not_found' && errorNotification();
  };

  const onCardChange = (card?: CardPaymentMethod) => {
    onSelectedCardChange?.(card);
    setSelectedCreditCard(card);
    if (card) {
      createNewSession(card.token);
    }
  };

  const handleSelectedCardChange = (card?: CardPaymentMethod) => {
    if (card) {
      onCardChange(card);
      setShowTokenizationForm(false);
    } else {
      setShowTokenizationForm(true);
      onCardChange();
    }
  };

  const onEvent = (event: CardNewMandateEvent) => {
    if (event.type === 'challenge_required') {
      setIsSubmitting(false);
    }
    setCvvEvent(event);
    onEventCVV?.(event);
    logger.log({
      description: 'CVV event launched',
      parameters: event,
    });
  };

  return (
    <div className="CreditCards-List">
      <CardNewMandate
        onStartLoading={() => setIsCVVLoading(true)}
        onEndLoading={() => setIsCVVLoading(false)}
        onSuccess={onSuccessCVVValidation}
        onError={onErrorCVVValidation}
        sessionId={sessionId}
        environment={FLYWIRE_JS_ENVIRONMENT}
        ref={cvvRef}
        onEvent={onEvent}
        currency={currency}
      >
        {isSubmitting ? (
          <Spinner className="CreditCards-List-spinner" />
        ) : (
          <ul data-testid="creditCardList" className="m-0">
            {cards.map((card, index) => (
              <CreditCardListItem
                card={card}
                key={`${card.details.lastFour}-${index}`}
                isCvvLoading={isCVVLoading}
                isSelected={selectedCreditCard?.token === card.token}
                onSelect={() => handleSelectedCardChange(card)}
              />
            ))}
            {hasAddForm && (
              <CreditCardListItem
                key="addCard"
                onSelect={() => handleSelectedCardChange()}
                isSelected={showTokenizationForm}
              />
            )}
          </ul>
        )}
      </CardNewMandate>
    </div>
  );
};

const Disclaimer = () => {
  const i18n = useTranslations();
  return (
    <p data-testid="disclaimer" className="CreditCards-Disclaimer">
      {i18n.t('recurring.payment.action.pay.description')}
    </p>
  );
};

type PayButtonProps = {
  isLoading?: boolean;
  payButtonLabel: string;
};

const Actions = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { showTokenizationForm, cvvEvent } = useCreditCards();

  const showPayButton =
    !showTokenizationForm && cvvEvent?.type !== 'challenge_required';

  return showPayButton ? (
    <div className={classNames(className, 'CreditCards-Actions')}>
      {children}
    </div>
  ) : null;
};

const PayButton = ({ isLoading = false, payButtonLabel }: PayButtonProps) => {
  const { cvvRef, isCVVLoading, setIsSubmitting } = useCreditCards();
  const dispatch = useDispatch();

  const _onPay = () => {
    if (cvvRef.current) {
      setIsSubmitting(true);
      cvvRef.current.submit();
    } else {
      dispatch(
        newNotification({
          message: 'notifications.error_card_selection_validation',
          type: 'error',
        }),
      );
    }
  };

  return (
    <Button
      tone="primary"
      size="full"
      onClick={_onPay}
      disabled={isLoading || isCVVLoading}
      data-testid="payBtn"
    >
      {payButtonLabel}
    </Button>
  );
};

type CreditCardsProps = {
  cards?: CardPaymentMethod[];
  cvvRef?: RefObject<CardNewMandateRef>;
};

const CreditCards = ({
  children,
  ...props
}: PropsWithChildren<CreditCardsProps>) => {
  return <CreditCardsProvider {...props}>{children}</CreditCardsProvider>;
};

CreditCards.CardList = CreditCardList;
CreditCards.Disclaimer = Disclaimer;
CreditCards.Actions = Actions;
CreditCards.PayButton = PayButton;
CreditCards.AddCardElement = AddCardElement;

export { CreditCards, type AddCardElementProps };
